import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Steps } from 'primereact/steps';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CarteV2Service from '../../../../services/CarteV2Service';
import '../CarteV2.css';

const GenerationIndividuelle = () => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);

    // Recherche bénéficiaire
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

    // Dialog nouveau bénéficiaire
    const [showNewBeneficiaryDialog, setShowNewBeneficiaryDialog] = useState(false);
    const [newBeneficiaryData, setNewBeneficiaryData] = useState({
        firstName: '',
        lastName: '',
        birthdate: null,
        numeroPolice: '',
        numeroAssurer: '',
        email: '',
        phoneNumber: '',
        sexe: 'M',
        country: '', // Code pays
        subscriberName: '', // Nom du souscripteur
        mainInsuredName: '', // Assuré principal (nom complet)
        mainInsuredNumeroAssurer: '', // Numéro assuré de l'assuré principal
        relationshipWithMainInsured: '', // Lien de parenté
        clientPhoto: null // Base64
    });
    const [newBeneficiaryPhoto, setNewBeneficiaryPhoto] = useState(null);
    const [newBeneficiaryPhotoPreview, setNewBeneficiaryPhotoPreview] = useState(null);

    // Dialog édition bénéficiaire
    const [showEditBeneficiaryDialog, setShowEditBeneficiaryDialog] = useState(false);
    const [editBeneficiaryData, setEditBeneficiaryData] = useState(null);
    const [editBeneficiaryPhoto, setEditBeneficiaryPhoto] = useState(null);
    const [editBeneficiaryPhotoPreview, setEditBeneficiaryPhotoPreview] = useState(null);

    // Liste des assurés principaux
    const [principalBeneficiaries, setPrincipalBeneficiaries] = useState([]);
    const [principalBeneficiariesLoading, setPrincipalBeneficiariesLoading] = useState(false);

    // Données de la carte
    const [formData, setFormData] = useState({
        numeroAssure: '',
        nom: '',
        prenom: '',
        dateNaissance: null,
        numeroPolice: '',
        souscripteur: '', // Nom du souscripteur
        assurePrincipalName: '', // Nom de l'assuré principal
        pays: '', // Sera défini par l'API
        priorite: 'NORMAL',
        serialNumber: '',
        notes: '',
        templateId: null,
        stockId: null,
        batchId: null
    });

    // Photo
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // Templates
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [frontPreviewUrl, setFrontPreviewUrl] = useState(null);
    const [backPreviewUrl, setBackPreviewUrl] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null); // Kept for backward compatibility
    const [cardSide, setCardSide] = useState('Front'); // Front or Back

    // Stocks
    const [stocks, setStocks] = useState([]);
    const [stocksLoading, setStocksLoading] = useState(false);

    // Batches
    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    // Countries
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);

    const steps = [
        { label: t('cardGeneration.individual.steps.searchBeneficiary') },
        { label: t('cardGeneration.individual.steps.cardInformation') },
        { label: t('cardGeneration.individual.steps.confirmation') }
    ];

    const priorityOptions = [
        { label: t('cardGeneration.individual.priority.normal'), value: 'NORMAL' },
        { label: t('cardGeneration.individual.priority.high'), value: 'HIGH' },
        { label: t('cardGeneration.individual.priority.urgent'), value: 'URGENT' }
    ];

    // Dynamic country options from API
    const countryOptions = countries.map(country => ({
        label: country.name || country.Name,
        value: country.code || country.Code,
        id: country.id || country.Id
    }));

    const genderOptions = [
        { label: t('cardGeneration.individual.gender.male'), value: 'M' },
        { label: t('cardGeneration.individual.gender.female'), value: 'F' }
    ];

    const relationshipOptions = [
        { label: t('cardGeneration.individual.relationship.principal') || 'Principal', value: 'PRINCIPAL' },
        { label: t('cardGeneration.individual.relationship.spouse'), value: 'SPOUSE' },
        { label: t('cardGeneration.individual.relationship.dependent'), value: 'DEPENDENT' }
    ];

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    // Charger les templates disponibles
    useEffect(() => {
        loadTemplates();
        loadStocks();
    }, [formData.pays]);

    // Charger les batches et les pays une seule fois au montage du composant
    useEffect(() => {
        loadBatches();
        loadCountries();
    }, []);

    // Nettoyer l'URL blob quand le composant est démonté
    useEffect(() => {
        return () => {
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
            if (newBeneficiaryPhotoPreview && newBeneficiaryPhotoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(newBeneficiaryPhotoPreview);
            }
            if (editBeneficiaryPhotoPreview && editBeneficiaryPhotoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(editBeneficiaryPhotoPreview);
            }
        };
    }, [photoPreview, newBeneficiaryPhotoPreview, editBeneficiaryPhotoPreview]);

    const loadTemplates = async () => {
        try {
            const filters = {
                country: formData.pays,
                isActive: true
            };
            const data = await CarteV2Service.getTemplates(filters);
            console.log('Templates loaded:', data);
            setTemplates(data || []);

            // Sélectionner le template par défaut si disponible
            if (data && data.length > 0) {
                const defaultTemplate = data.find(t => t.isDefault || t.IsDefault);
                if (defaultTemplate && !formData.templateId) {
                    const templateId = defaultTemplate.id || defaultTemplate.Id;
                    setFormData({ ...formData, templateId });
                    setSelectedTemplate(defaultTemplate);
                } else if (!formData.templateId) {
                    // Si pas de template par défaut, sélectionner le premier
                    const firstTemplate = data[0];
                    const templateId = firstTemplate.id || firstTemplate.Id;
                    setFormData({ ...formData, templateId });
                    setSelectedTemplate(firstTemplate);
                }
            }
        } catch (error) {
            console.error('Erreur chargement templates:', error);
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.loadTemplates'));
            setTemplates([]);
        }
    };

    const loadStocks = async () => {
        setStocksLoading(true);
        try {
            const stocksData = await CarteV2Service.getAvailableStocks();
            setStocks(stocksData || []);
        } catch (error) {
            console.error('Erreur chargement stocks:', error);
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.loadStocks'));
            setStocks([]);
        } finally {
            setStocksLoading(false);
        }
    };

    const loadBatches = async () => {
        setBatchesLoading(true);
        try {
            // Charger les 10 batches les plus récents sans filtre de pays
            const batchesData = await CarteV2Service.getCardBatches({
                pageSize: 10,
                pageNumber: 1
            });
            setBatches(batchesData || []);
        } catch (error) {
            console.error('Erreur chargement batches:', error);
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.loadBatches'));
            setBatches([]);
        } finally {
            setBatchesLoading(false);
        }
    };

    // Charger les pays disponibles
    const loadCountries = async () => {
        setCountriesLoading(true);
        try {
            const data = await CarteV2Service.getCountries();
            const countriesData = Array.isArray(data) ? data : [];

            setCountries(countriesData);
            console.log('Pays chargés:', countriesData.length);

            // Si aucun pays n'est sélectionné et qu'il y a des pays disponibles, sélectionner le premier
            if (countriesData.length > 0) {
                setFormData(prev => {
                    // Ne mettre à jour que si pas de pays déjà sélectionné
                    if (!prev.pays) {
                        const firstCountry = countriesData[0];
                        const countryCode = firstCountry.code || firstCountry.Code;
                        return { ...prev, pays: countryCode };
                    }
                    return prev;
                });
            }
        } catch (error) {
            console.error('Erreur chargement pays:', error);
            showToast('error', t('common.toast.error'), 'Erreur lors du chargement des pays');
            setCountries([]);
        } finally {
            setCountriesLoading(false);
        }
    };

    // Charger les assurés principaux
    const loadPrincipalBeneficiaries = async () => {
        setPrincipalBeneficiariesLoading(true);
        try {
            const data = await CarteV2Service.getPrincipalBeneficiaries();
            const beneficiaries = Array.isArray(data) ? data : [];

            setPrincipalBeneficiaries(beneficiaries);
            console.log('Assurés principaux chargés:', beneficiaries.length);
        } catch (error) {
            console.error('Erreur chargement assurés principaux:', error);
            showToast('error', t('common.toast.error'), 'Erreur lors du chargement des assurés principaux');
            setPrincipalBeneficiaries([]);
        } finally {
            setPrincipalBeneficiariesLoading(false);
        }
    };

    // Recherche de bénéficiaires
    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.searchTerm'));
            return;
        }

        setLoading(true);
        try {
            const response = await CarteV2Service.searchClientByKeyword(searchTerm.trim());
            const data = response.data || response;
            const results = Array.isArray(data) ? data : [];
            setSearchResults(results);

            if (results.length === 0) {
                showToast('info', t('cardGeneration.individual.info.search'), t('cardGeneration.individual.info.noBeneficiaryFound'));
            } else {
                showToast('success', t('cardGeneration.individual.info.search'), t('cardGeneration.individual.success.searchResults', { count: results.length }));
            }
        } catch (error) {
            console.error('Erreur recherche:', error);
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.search') + ': ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Gestion de l'upload de photo pour nouveau bénéficiaire
    const onNewBeneficiaryPhotoSelect = async (e) => {
        const file = e.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.imageOnly'));
                e.options.clear();
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.imageSize'));
                e.options.clear();
                return;
            }

            // Nettoyer l'ancienne URL blob
            if (newBeneficiaryPhotoPreview) {
                URL.revokeObjectURL(newBeneficiaryPhotoPreview);
            }

            setNewBeneficiaryPhoto(file);
            const previewUrl = URL.createObjectURL(file);
            setNewBeneficiaryPhotoPreview(previewUrl);

            // Convertir en base64
            try {
                const base64 = await imageToBase64(file);
                setNewBeneficiaryData({ ...newBeneficiaryData, clientPhoto: base64 });
                showToast('success', t('cardGeneration.individual.labels.photo'), t('cardGeneration.individual.success.photoUploaded'));
            } catch (error) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.photoConversion'));
            }

            if (e.options.clear) {
                e.options.clear();
            }
        }
    };

    // Créer un nouveau bénéficiaire
    const handleCreateBeneficiary = async () => {
        // Validation
        if (!newBeneficiaryData.firstName.trim() || !newBeneficiaryData.lastName.trim()) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.nameRequired'));
            return;
        }

        if (!newBeneficiaryData.numeroPolice.trim()) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.policeNumberRequired'));
            return;
        }

        // Le numéro assuré est maintenant optionnel - le backend le générera si non fourni

        if (!newBeneficiaryData.birthdate) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.birthdateRequired'));
            return;
        }

        if (!newBeneficiaryData.subscriberName.trim()) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.subscriberNameRequired'));
            return;
        }

        if (!newBeneficiaryData.relationshipWithMainInsured) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.relationshipRequired'));
            return;
        }

        // Si ce n'est pas l'assuré principal, le mainInsuredName est obligatoire
        if (newBeneficiaryData.relationshipWithMainInsured !== 'PRINCIPAL' && !newBeneficiaryData.mainInsuredName.trim()) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.mainInsuredNameRequired'));
            return;
        }

        // Si c'est l'assuré principal, auto-remplir mainInsuredName avec son propre nom
        if (newBeneficiaryData.relationshipWithMainInsured === 'PRINCIPAL') {
            const fullName = `${newBeneficiaryData.firstName.trim()} ${newBeneficiaryData.lastName.trim()}`.trim();
            newBeneficiaryData.mainInsuredName = fullName;
            newBeneficiaryData.mainInsuredNumeroAssurer = newBeneficiaryData.numeroAssure|| '';
        }

        setLoading(true);
        try {
            // Formater la date de naissance sans décalage de fuseau horaire
            const birthDate = new Date(newBeneficiaryData.birthdate);
            const year = birthDate.getFullYear();
            const month = String(birthDate.getMonth() + 1).padStart(2, '0');
            const day = String(birthDate.getDate()).padStart(2, '0');
            const birthDateISO = `${year}-${month}-${day}T12:00:00.000Z`;

            // Structure conforme au format attendu par l'API (voir detail.md)
            const enrollmentData = {
                beneficiaryId: 0,
                countryCode: newBeneficiaryData.country || formData.pays || "",
                photoPath: "",
                photoBase64: newBeneficiaryData.clientPhoto || "",
                createdBy: "",
                options: {
                    firstName: newBeneficiaryData.firstName.trim(),
                    lastName: newBeneficiaryData.lastName.trim(),
                    phoneNumber: newBeneficiaryData.phoneNumber.trim() || "",
                    email: newBeneficiaryData.email.trim() || "",
                    sexe: newBeneficiaryData.sexe,
                    birthdate: birthDateISO,
                    numeroPolice: newBeneficiaryData.numeroPolice.trim(),
                    souscripteur: newBeneficiaryData.subscriberName.trim() || "",
                    assurePrincipalName: newBeneficiaryData.mainInsuredName.trim() || "",
                    numeroBeneficiairePrincipal: newBeneficiaryData.mainInsuredNumeroAssurer || "",
                    lienParente: newBeneficiaryData.relationshipWithMainInsured || ""
                }
            };

            console.log('Création bénéficiaire:', enrollmentData);

            const response = await CarteV2Service.createBeneficiaryEnrollment(enrollmentData);
            const result = response.data || response;
            console.log('Résultat création:', result);

            if (result && (result.id || result.Id)) {
                showToast('success', t('common.toast.success'), t('cardGeneration.individual.success.beneficiaryCreated'));

                // Garder la preview de la photo pour l'utiliser après
                const photoPreviewToUse = newBeneficiaryPhotoPreview;
                const clientPhotoBase64 = newBeneficiaryData.clientPhoto;

                // Extraire les données retournées par l'API
                const createdBeneficiaryId = result.id || result.Id;
                const createdIdClient = result.idClient || result.IdClient;
                const numeroAssure = result.numeroAssure || result.NumeroAssure;

                if (!createdBeneficiaryId) {
                    console.error('Aucun ID retourné par l\'API:', result);
                    showToast('error', t('common.toast.error'), 'ID bénéficiaire manquant dans la réponse');
                    return;
                }

                console.log('ID du bénéficiaire créé:', createdBeneficiaryId);
                console.log('ID Client:', createdIdClient);
                console.log('Numéro Assuré:', numeroAssure);

                // Fermer le dialog
                setShowNewBeneficiaryDialog(false);

                // Sélectionner automatiquement le nouveau bénéficiaire avec toutes les données de la réponse API
                const newBeneficiary = {
                    id: createdBeneficiaryId, // ID interne de l'enregistrement
                    idClient: createdIdClient, // ID Client = Numéro assuré
                    numeroAssure: createdIdClient, // Numéro assuré = idClient
                    firstName: enrollmentData.options.firstName,
                    lastName: enrollmentData.options.lastName,
                    birthDate: enrollmentData.options.birthdate,
                    policeNumber: enrollmentData.options.numeroPolice,
                    souscripteur: enrollmentData.options.souscripteur,
                    email: enrollmentData.options.email,
                    phone: enrollmentData.options.phoneNumber,
                    clientPhoto: enrollmentData.photoBase64, // Utiliser le base64 de la photo
                    photoUrl: photoPreviewToUse // Fallback si pas de clientPhoto
                };

                // Réinitialiser le formulaire (après avoir créé l'objet newBeneficiary)
                setNewBeneficiaryData({
                    firstName: '',
                    lastName: '',
                    birthdate: null,
                    numeroPolice: '',
                    numeroAssurer: '',
                    email: '',
                    phoneNumber: '',
                    sexe: 'M',
                    country: '',
                    subscriberName: '',
                    mainInsuredName: '',
                    mainInsuredNumeroAssurer: '',
                    relationshipWithMainInsured: '',
                    clientPhoto: null
                });
                setNewBeneficiaryPhoto(null);
                setNewBeneficiaryPhotoPreview(null);

                handleSelectBeneficiary(newBeneficiary);
            } else {
                const errorMsg = result.message || result.errors?.[0] || t('cardGeneration.individual.errors.createBeneficiary');
                showToast('error', t('common.toast.error'), errorMsg);
            }
        } catch (error) {
            console.error('Erreur création bénéficiaire:', error);
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.createBeneficiary') + ': ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Ouvrir le dialogue d'édition d'un bénéficiaire
    const handleEditBeneficiary = (beneficiary) => {

        // Gérer la date de naissance
        const birthDateValue = beneficiary.birthdate || beneficiary.birthDate || beneficiary.dateNaissance;
        let parsedDate = null;
        if (birthDateValue) {
            try {
                parsedDate = new Date(birthDateValue);
            } catch (error) {
                console.warn('Erreur parsing date:', error);
            }
        }

        // Préparer les données pour l'édition
        setEditBeneficiaryData({
            id: beneficiary.id || beneficiary.Id,
            idClient: beneficiary.idClient || beneficiary.IdClient || '',
            firstName: beneficiary.firstName || beneficiary.prenom || '',
            lastName: beneficiary.lastName || beneficiary.nom || '',
            birthdate: parsedDate,
            numeroPolice: beneficiary.policeNumber || beneficiary.numeroPolice || '',
            numeroAssurer: beneficiary.numeroAssure || beneficiary.idClient || '',
            email: beneficiary.email || '',
            sexe: beneficiary.sexe || beneficiary.gender || 'M',
            subscriberName: beneficiary.subscriberName || beneficiary.souscripteur || '',
            mainInsuredName: beneficiary.mainInsuredName || beneficiary.assurePrincipalName || '',
            mainInsuredNumeroAssurer: beneficiary.mainInsuredNumeroAssurer || beneficiary.principalBeneficiaryNumber || '',
            relationshipWithMainInsured: beneficiary.relationshipWithMainInsured || beneficiary.lienParente || '',
            clientPhoto: beneficiary.clientPhoto || beneficiary.clientphoto || null
        });

        // Charger la photo existante si disponible
        const photoUrl = beneficiary.clientPhoto || beneficiary.clientphoto || beneficiary.photoUrl;
        if (photoUrl) {
            if (photoUrl.startsWith('http') || photoUrl.startsWith('data:')) {
                setEditBeneficiaryPhotoPreview(photoUrl);
            } else if (photoUrl.length > 100) {
                setEditBeneficiaryPhotoPreview(`data:image/jpeg;base64,${photoUrl}`);
            }
        } else {
            setEditBeneficiaryPhotoPreview(null);
        }

        setEditBeneficiaryPhoto(null);
        setShowEditBeneficiaryDialog(true);

        // Charger la liste des assurés principaux
        loadPrincipalBeneficiaries();
    };

    // Gestion de l'upload de photo pour l'édition
    const onEditBeneficiaryPhotoSelect = async (e) => {
        const file = e.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.imageOnly'));
                e.options.clear();
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.imageSize'));
                e.options.clear();
                return;
            }

            // Nettoyer l'ancienne URL blob
            if (editBeneficiaryPhotoPreview && editBeneficiaryPhotoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(editBeneficiaryPhotoPreview);
            }

            setEditBeneficiaryPhoto(file);
            const previewUrl = URL.createObjectURL(file);
            setEditBeneficiaryPhotoPreview(previewUrl);

            // Convertir en base64
            try {
                const base64 = await imageToBase64(file);
                setEditBeneficiaryData({ ...editBeneficiaryData, clientPhoto: base64 });
                showToast('success', t('cardGeneration.individual.labels.photo'), t('cardGeneration.individual.success.photoUploaded'));
            } catch (error) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.photoConversion'));
            }

            if (e.options.clear) {
                e.options.clear();
            }
        }
    };

    // Mettre à jour un bénéficiaire
    const handleUpdateBeneficiary = async () => {
        if (!editBeneficiaryData) return;

        // Validation
        if (!editBeneficiaryData.firstName.trim() || !editBeneficiaryData.lastName.trim()) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.nameRequired'));
            return;
        }

        if (!editBeneficiaryData.birthdate) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.birthdateRequired'));
            return;
        }

        setLoading(true);
        try {
            // Formater la date de naissance sans décalage de fuseau horaire
            // Utiliser l'heure locale à midi pour éviter les problèmes de fuseau horaire
            const birthDate = new Date(editBeneficiaryData.birthdate);
            const year = birthDate.getFullYear();
            const month = String(birthDate.getMonth() + 1).padStart(2, '0');
            const day = String(birthDate.getDate()).padStart(2, '0');
            const birthDateISO = `${year}-${month}-${day}T12:00:00.000Z`;

            // Préparer les données selon le format de l'API
            const updateData = {
                firstName: editBeneficiaryData.firstName.trim(),
                lastName: editBeneficiaryData.lastName.trim(),
                email: editBeneficiaryData.email.trim() || "",
                sexe: editBeneficiaryData.sexe,
                birthdate: birthDateISO,
                numeroPolice: editBeneficiaryData.numeroPolice || "",
                numeroAssure: editBeneficiaryData.numeroAssurer || "",
                souscripteur: editBeneficiaryData.subscriberName || "",
                assurePrincipalName: editBeneficiaryData.mainInsuredName || "",
                numeroBeneficiairePrincipal: editBeneficiaryData.mainInsuredNumeroAssurer || "",
                lienParente: editBeneficiaryData.relationshipWithMainInsured || "",
                clientPhoto: editBeneficiaryData.clientPhoto || ""
            };

            console.log('Mise à jour bénéficiaire:', updateData);

            const response = await CarteV2Service.updateBeneficiaryProfile(editBeneficiaryData.id, updateData);
            const result = response.data || response;
            console.log('Résultat mise à jour:', result);

            // Vérifier si la réponse est un succès
            const isSuccess = result.isSuccess !== undefined ? result.isSuccess : result.IsSuccess !== undefined ? result.IsSuccess : true;
            const message = result.message || result.Message;
            const errors = result.errors || result.Errors || [];

            if (!isSuccess) {
                // Formater les erreurs
                let errorMessage = message || t('cardGeneration.individual.errors.updateBeneficiary');
                let errorDetails = null;

                if (errors && errors.length > 0) {
                    errorDetails = errors.map(err => `• ${err}`).join('\n');
                }

                // Afficher l'erreur
                if (errorDetails) {
                    showToast('error', errorMessage, errorDetails, 10000);
                } else {
                    showToast('error', t('common.toast.error'), errorMessage);
                }
                return;
            }

            // Succès
            showToast('success', t('common.toast.success'), message || t('cardGeneration.individual.success.beneficiaryUpdated'));

            // Fermer le dialogue
            setShowEditBeneficiaryDialog(false);

            // Nettoyer les données
            setEditBeneficiaryData(null);
            setEditBeneficiaryPhoto(null);
            if (editBeneficiaryPhotoPreview && editBeneficiaryPhotoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(editBeneficiaryPhotoPreview);
            }
            setEditBeneficiaryPhotoPreview(null);

            // Rafraîchir la liste des résultats de recherche
            if (searchTerm) {
                await handleSearch();
            }
        } catch (error) {
            console.error('Erreur mise à jour bénéficiaire:', error);

            // Parse and format error message
            let errorMessage = t('cardGeneration.individual.errors.updateBeneficiary');
            let errorDetails = null;

            try {
                let errorData = null;

                if (error.response && error.response.data) {
                    errorData = error.response.data;
                } else if (error.message && error.message.includes('{')) {
                    const jsonStart = error.message.indexOf('{');
                    const jsonStr = error.message.substring(jsonStart);
                    errorData = JSON.parse(jsonStr);
                }

                if (errorData) {
                    errorMessage = errorData.message || errorData.Message || t('cardGeneration.individual.errors.updateBeneficiary');

                    const errors = errorData.errors || errorData.Errors;
                    if (errors && Array.isArray(errors) && errors.length > 0) {
                        errorDetails = errors.map(err => `• ${err}`).join('\n');
                    }
                }
            } catch (parseError) {
                console.error('Erreur lors du parsing de l\'erreur:', parseError);
                errorMessage = error.message || t('cardGeneration.individual.errors.updateBeneficiary');
            }

            if (errorDetails) {
                showToast('error', errorMessage, errorDetails, 10000);
            } else {
                showToast('error', t('common.toast.error'), errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // Sélection d'un bénéficiaire
    const handleSelectBeneficiary = (beneficiary) => {
        console.log('Bénéficiaire sélectionné:', beneficiary);

        setSelectedBeneficiary(beneficiary);

        // Extraire l'ID client avec priorité à idClient
        const clientId = beneficiary.idClient || beneficiary.IdClient ||
                        beneficiary.id || beneficiary.Id ||
                        beneficiary.numeroAssure || beneficiary.code || '';

        // Gérer la date de naissance - peut être birthdate ou dateNaissance
        const birthDateValue = beneficiary.birthdate || beneficiary.birthDate || beneficiary.dateNaissance;
        let parsedDate = null;
        if (birthDateValue) {
            try {
                parsedDate = new Date(birthDateValue);
            } catch (error) {
                console.warn('Erreur parsing date:', error);
            }
        }

        setFormData({
            ...formData,
            numeroAssure: beneficiary.numeroAssure || beneficiary.idClient,
            nom: beneficiary.lastName || beneficiary.nom || '',
            prenom: beneficiary.firstName || beneficiary.prenom || '',
            dateNaissance: parsedDate,
            numeroPolice: beneficiary.policeNumber || beneficiary.numeroPolice || '',
            souscripteur: beneficiary.souscripteur || beneficiary.subscriberName || '',
            assurePrincipalName: beneficiary.assurePrincipalName || '',
            pays: beneficiary.country || beneficiary.pays || beneficiary.countryCode || 'GN'
        });

        console.log('FormData mis à jour:', {
            numeroAssure: clientId.toString(),
            nom: beneficiary.lastName || beneficiary.nom,
            prenom: beneficiary.firstName || beneficiary.prenom,
            dateNaissance: parsedDate
        });

        // Load existing photo - gérer base64 et URLs
        let photoData = null;

        // Fonction helper pour vérifier si c'est une URL valide
        const isValidUrl = (string) => {
            try {
                new URL(string);
                return true;
            } catch (_) {
                return false;
            }
        };

        // Vérifier d'abord clientphoto (priorité car mentionné dans votre demande)
        if (beneficiary.clientphoto && typeof beneficiary.clientphoto === 'string') {
            // Si c'est une URL (comme https://facialstorage.blob.core.windows.net/...)
            if (isValidUrl(beneficiary.clientphoto)) {
                photoData = beneficiary.clientphoto;
                console.log('Photo URL trouvée dans clientphoto:', beneficiary.clientphoto);
            }
            // Si c'est un data URL complet
            else if (beneficiary.clientphoto.startsWith('data:')) {
                photoData = beneficiary.clientphoto;
                console.log('Photo data URL trouvée dans clientphoto');
            }
            // Si c'est juste du base64 sans préfixe (longueur > 100 pour éviter les faux positifs)
            else if (beneficiary.clientphoto.length > 100 && !beneficiary.clientphoto.includes('http')) {
                photoData = `data:image/jpeg;base64,${beneficiary.clientphoto}`;
                console.log('Photo base64 trouvée dans clientphoto, convertie en data URL');
            }
        }

        // Fallback sur clientPhoto (avec majuscule) si clientphoto n'a rien donné
        if (!photoData && beneficiary.clientPhoto && typeof beneficiary.clientPhoto === 'string') {
            if (isValidUrl(beneficiary.clientPhoto)) {
                photoData = beneficiary.clientPhoto;
                console.log('Photo URL trouvée dans clientPhoto:', beneficiary.clientPhoto);
            }
            else if (beneficiary.clientPhoto.startsWith('data:')) {
                photoData = beneficiary.clientPhoto;
                console.log('Photo data URL trouvée dans clientPhoto');
            }
            else if (beneficiary.clientPhoto.length > 100 && !beneficiary.clientPhoto.includes('http')) {
                photoData = `data:image/jpeg;base64,${beneficiary.clientPhoto}`;
                console.log('Photo base64 trouvée dans clientPhoto, convertie en data URL');
            }
        }

        // Sinon vérifier les autres attributs classiques
        if (!photoData) {
            const photoSources = [
                beneficiary.photoUrl,
                beneficiary.PhotoUrl,
                beneficiary.photo,
                beneficiary.Photo,
                beneficiary.photoPath,
                beneficiary.PhotoPath
            ];

            for (const source of photoSources) {
                if (source && typeof source === 'string') {
                    if (isValidUrl(source)) {
                        photoData = source;
                        console.log('Photo URL trouvée dans autres attributs:', source);
                        break;
                    }
                    else if (source.startsWith('data:')) {
                        photoData = source;
                        console.log('Photo data URL trouvée dans autres attributs');
                        break;
                    }
                    else if (source.length > 100 && !source.includes('http')) {
                        photoData = `data:image/jpeg;base64,${source}`;
                        console.log('Photo base64 trouvée dans autres attributs, convertie en data URL');
                        break;
                    }
                }
            }
        }

        console.log('Photo finale trouvée:', photoData ? 'Oui (type: ' + (photoData.startsWith('data:') ? 'base64' : 'URL') + ')' : 'Non');

        if (photoData) {
            // Clean up old preview (uniquement si c'est une URL blob)
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
            setPhotoPreview(photoData);
            setPhoto(null); // No file object, using existing URL/base64
            showToast('info', t('cardGeneration.individual.labels.photo'), t('cardGeneration.individual.info.photoLoaded'));
        } else {
            // Reset photo if no existing photo
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
            setPhotoPreview(null);
            setPhoto(null);
            console.log('Aucune photo trouvée pour ce bénéficiaire');
        }

        setActiveStep(1);
        const fullName = `${beneficiary.firstName || beneficiary.prenom} ${beneficiary.lastName || beneficiary.nom}`;
        showToast('success', t('cardGeneration.individual.info.selection'), t('cardGeneration.individual.success.beneficiarySelected', { name: fullName }));
    };

    // Gestion de l'upload de photo
    const onPhotoSelect = (e) => {
        const file = e.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.imageOnly'));
                e.options.clear(); // Clear the file upload
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.imageSize'));
                e.options.clear(); // Clear the file upload
                return;
            }

            // Nettoyer l'ancienne URL blob si elle existe
            if (photoPreview) {
                URL.revokeObjectURL(photoPreview);
            }

            setPhoto(file);
            const newPreviewUrl = URL.createObjectURL(file);
            setPhotoPreview(newPreviewUrl);
            showToast('success', t('cardGeneration.individual.labels.photo'), t('cardGeneration.individual.success.photoUploaded'));

            // Clear the file input to allow selecting the same file again
            if (e.options.clear) {
                e.options.clear();
            }
        }
    };

    // Gestion du template
    const handleTemplateChange = (e) => {
        const templateId = e.value;
        setFormData({ ...formData, templateId });
        const template = templates.find(t => (t.id || t.Id) === templateId);
        setSelectedTemplate(template);

        if (template) {
            const templateName = template.templateName || template.TemplateName ||
                                template.name || template.Name || `Template ${templateId}`;
            console.log('Template sélectionné:', templateName, 'ID:', templateId);
        }
    };

    // Convertir une image en base64
    const imageToBase64 = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Générer une image placeholder (carré gris 300x400)
    const generatePlaceholderImage = () => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            canvas.width = 300;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');

            // Fond gris
            ctx.fillStyle = '#E0E0E0';
            ctx.fillRect(0, 0, 300, 400);

            // Icône silhouette
            ctx.fillStyle = '#BDBDBD';
            ctx.beginPath();
            ctx.arc(150, 120, 50, 0, Math.PI * 2); // Tête
            ctx.fill();
            ctx.beginPath();
            ctx.ellipse(150, 250, 80, 100, 0, 0, Math.PI * 2); // Corps
            ctx.fill();

            // Texte
            ctx.fillStyle = '#757575';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('PHOTO', 150, 340);
            ctx.fillText('PREVIEW', 150, 360);

            // Convertir en base64
            const base64 = canvas.toDataURL('image/png').split(',')[1];
            resolve(base64);
        });
    };

    // Télécharger une image
    const downloadImage = async (url, fileName) => {
        if (url.startsWith('blob:') || url.startsWith('data:')) {
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        }
    };

    // Télécharger les deux images de la carte
    const handleDownloadCardImage = async () => {
        if (!frontPreviewUrl && !backPreviewUrl) {
            showToast('warn', t('common.toast.warning'), 'Aucune image à télécharger');
            return;
        }

        try {
            const baseName = `Carte_${formData.prenom}_${formData.nom}`;
            const timestamp = Date.now();

            // Télécharger les deux faces
            if (frontPreviewUrl) {
                await downloadImage(frontPreviewUrl, `${baseName}_Front_${timestamp}.png`);
            }

            // Petit délai entre les deux téléchargements pour éviter les problèmes de navigateur
            await new Promise(resolve => setTimeout(resolve, 300));

            if (backPreviewUrl) {
                await downloadImage(backPreviewUrl, `${baseName}_Back_${timestamp}.png`);
            }

            showToast('success', t('common.toast.success'), 'Images téléchargées avec succès');
        } catch (error) {
            console.error('Erreur téléchargement images:', error);
            showToast('error', t('common.toast.error'), 'Erreur lors du téléchargement des images');
        }
    };

    // Imprimer les deux faces de la carte
    const handlePrintCard = () => {
        if (!frontPreviewUrl && !backPreviewUrl) {
            showToast('warn', t('common.toast.warning'), 'Aucune image à imprimer');
            return;
        }

        // Créer une fenêtre d'impression avec les deux images
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showToast('error', t('common.toast.error'), 'Impossible d\'ouvrir la fenêtre d\'impression');
            return;
        }

        const cardName = `${formData.prenom} ${formData.nom}`;
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Impression Carte - ${cardName}</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }

                    body {
                        font-family: Arial, sans-serif;
                    }

                    .print-page {
                        page-break-after: always;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        padding: 20px;
                    }

                    .print-page:last-child {
                        page-break-after: auto;
                    }

                    .card-container {
                        text-align: center;
                        width: 100%;
                        max-width: 1000px;
                    }

                    .card-title {
                        font-size: 18px;
                        font-weight: bold;
                        margin-bottom: 20px;
                        color: #333;
                    }

                    .card-image {
                        max-width: 100%;
                        height: auto;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        border-radius: 8px;
                    }

                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                        }

                        .print-page {
                            page-break-after: always;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            width: 100%;
                            height: 100%;
                            padding: 0;
                            margin: 0;
                        }

                        .print-page:last-child {
                            page-break-after: auto;
                        }

                        .card-container {
                            width: 100%;
                            height: 100%;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                        }

                        .card-title {
                            display: none; /* Masquer le titre en impression pour format carte pur */
                        }

                        .card-image {
                            width: 100%;
                            height: 100%;
                            object-fit: contain;
                            box-shadow: none;
                            border-radius: 3.18mm; /* Bordure arrondie standard des cartes (CR80) */
                        }

                        @page {
                            /* Format carte suggéré par défaut (CR80/ID-1: 85.60mm x 53.98mm) */
                            /* L'utilisateur peut modifier dans le dialog d'impression */
                            margin: 0;
                        }
                    }

                    @media screen {
                        .no-print-message {
                            position: fixed;
                            top: 20px;
                            right: 20px;
                            background: #007bff;
                            color: white;
                            padding: 10px 20px;
                            border-radius: 5px;
                            font-size: 14px;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                        }
                    }

                    @media print {
                        .no-print-message {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="no-print-message">
                    Appuyez sur Ctrl+P ou Cmd+P pour imprimer
                </div>

                ${frontPreviewUrl ? `
                <div class="print-page">
                    <div class="card-container">
                        <h2 class="card-title">Carte - ${cardName} - Face Avant</h2>
                        <img src="${frontPreviewUrl}" alt="Face Avant" class="card-image" />
                    </div>
                </div>
                ` : ''}

                ${backPreviewUrl ? `
                <div class="print-page">
                    <div class="card-container">
                        <h2 class="card-title">Carte - ${cardName} - Face Arrière</h2>
                        <img src="${backPreviewUrl}" alt="Face Arrière" class="card-image" />
                    </div>
                </div>
                ` : ''}

                <script>
                    // Auto-déclencher l'impression après le chargement des images
                    window.onload = function() {
                        // Attendre un court instant pour s'assurer que les images sont bien chargées
                        setTimeout(function() {
                            window.print();
                        }, 500);
                    };

                    // Fermer la fenêtre après l'impression (optionnel)
                    window.onafterprint = function() {
                        // Donner un délai avant de fermer pour permettre à l'utilisateur de voir le résultat
                        setTimeout(function() {
                            window.close();
                        }, 1000);
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    // Prévisualisation de la carte
    const handlePreview = async () => {
        if (!formData.templateId) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.templateRequired'));
            return;
        }

        if (!formData.prenom || !formData.nom) {
            showToast('warn', t('common.toast.warning'), t('cardGeneration.individual.validation.nameAndFirstNameRequired'));
            return;
        }

        setLoading(true);
        try {
            // Convertir la photo en base64 selon les différents cas
            let photoBase64 = null;

            // Cas 1: Un fichier photo a été uploadé (objet File)
            if (photo && photo instanceof File) {
                try {
                    photoBase64 = await imageToBase64(photo);
                    console.log('Photo convertie depuis File object');
                } catch (error) {
                    console.warn('Erreur conversion photo File:', error);
                    photoBase64 = await generatePlaceholderImage();
                }
            }
            // Cas 2: Une photo existe déjà en preview (base64 ou URL)
            else if (photoPreview) {
                // Si c'est déjà en base64
                if (photoPreview.startsWith('data:')) {
                    // Extraire seulement la partie base64 (après la virgule)
                    photoBase64 = photoPreview.split(',')[1];
                    console.log('Photo utilisée depuis photoPreview (base64)');
                }
                // Si c'est une URL, la convertir en base64
                else if (photoPreview.startsWith('http')) {
                    try {
                        photoBase64 = await urlToBase64(photoPreview);
                        console.log('Photo convertie depuis URL:', photoPreview);
                    } catch (error) {
                        console.warn('Erreur conversion URL vers base64:', error);
                        photoBase64 = await generatePlaceholderImage();
                    }
                }
                // Si c'est une URL blob locale
                else if (photoPreview.startsWith('blob:')) {
                    try {
                        photoBase64 = await blobUrlToBase64(photoPreview);
                        console.log('Photo convertie depuis blob URL');
                    } catch (error) {
                        console.warn('Erreur conversion blob URL:', error);
                        photoBase64 = await generatePlaceholderImage();
                    }
                }
            }
            // Cas 3: Pas de photo, utiliser placeholder
            else {
                console.log('Aucune photo disponible, utilisation du placeholder');
                photoBase64 = await generatePlaceholderImage();
            }

            const previewData = {
                clientId: selectedBeneficiary?.id || formData.id,
                firstName: formData.prenom,
                lastName: formData.nom,
                cardNumber: formData.numeroAssure || 'PREVIEW',
                birthDate: formData.dateNaissance || new Date().toISOString(),
                policyNumber: formData.numeroPolice || 'PREVIEW',
                souscripteur: formData.souscripteur || '',
                assurePrincipalName: formData.assurePrincipalName || '',
                photoBase64: photoBase64,
                beneficiaryId: selectedBeneficiary?.id || null,
                serialNumber: formData.serialNumber,
                nationality: formData.pays === 'CI' ? 'Ivoirienne' :
                            formData.pays === 'GN' ? 'Guinéenne C.' :
                            formData.pays === 'CD' ? 'Congolais' : 'Liberian',
                customFields: {
                    country: formData.pays,
                    priority: formData.priorite,
                    notes: formData.notes
                }
            };

            // Charger les deux faces simultanément
            const [frontUrl, backUrl] = await Promise.all([
                CarteV2Service.getTemplatePreviewUrl(formData.templateId, previewData, 'Front'),
                CarteV2Service.getTemplatePreviewUrl(formData.templateId, previewData, 'Back')
            ]);

            setFrontPreviewUrl(frontUrl);
            setBackPreviewUrl(backUrl);
            setPreviewUrl(frontUrl); // Pour compatibilité
            setShowPreviewDialog(true);
            showToast('success', t('cardGeneration.individual.labels.preview'), t('cardGeneration.individual.success.previewGenerated'));
        } catch (error) {
            console.error('Erreur prévisualisation:', error);
            const errorMsg = error.response?.data?.[0] || error.message || t('cardGeneration.individual.errors.unknown');
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.generatePreview') + ': ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Fonction helper pour convertir une URL en base64
    const urlToBase64 = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Important pour éviter les erreurs CORS
            
            img.onload = function() {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                canvas.width = this.naturalWidth;
                canvas.height = this.naturalHeight;
                
                ctx.drawImage(this, 0, 0);
                
                try {
                    const dataURL = canvas.toDataURL('image/jpeg', 0.8);
                    // Extraire seulement la partie base64
                    const base64 = dataURL.split(',')[1];
                    resolve(base64);
                } catch (error) {
                    reject(error);
                }
            };
            
            img.onerror = function() {
                reject(new Error('Impossible de charger l\'image depuis l\'URL'));
            };
            
            img.src = url;
        });
    };

    // Fonction helper pour convertir une blob URL en base64
    const blobUrlToBase64 = (blobUrl) => {
        return new Promise((resolve, reject) => {
            fetch(blobUrl)
                .then(response => response.blob())
                .then(blob => {
                    const reader = new FileReader();
                    reader.onload = function() {
                        // Extraire seulement la partie base64
                        const base64 = this.result.split(',')[1];
                        resolve(base64);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                })
                .catch(reject);
        });
    };

    // Génération de la carte
    const handleGenerate = async () => {
        // Validation
        if (!selectedBeneficiary?.id) {
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.beneficiaryRequired'));
            return;
        }

        if (!formData.templateId) {
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.templateRequired'));
            return;
        }

        if (!formData.stockId) {
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.validation.stockRequired'));
            return;
        }

        setLoading(true);
        try {
            // Préparer les données pour l'API selon CardProductionRequest
            const cardData = {
                beneficiaryId: parseInt(selectedBeneficiary.id), // Doit être un int
                countryCode: formData.pays,
                templateId: parseInt(formData.templateId), // Doit être un int
                stockId: parseInt(formData.stockId), // Stock sélectionné
                priority: formData.priorite,
                requestedBy: localStorage.getItem('userEmail') || 'admin@company.com',
                photoPath: photoPreview || null,
                paymentReference: null, // Pour cartes supplémentaires
                customData: {
                    serialNumber: formData.serialNumber,
                    notes: formData.notes,
                    numeroPolice: formData.numeroPolice,
                    souscripteur: formData.souscripteur || '',
                    assurePrincipalName: formData.assurePrincipalName || ''
                }
            };

            console.log('Envoi de la demande de production:', cardData);

            // Appeler l'API de création de carte
            const response = await CarteV2Service.initiateCardProductionDetailed(cardData);
            const result = response.data || response;

            if (result.isSuccess) {
                const cardId = result.cardId;
                showToast('success', t('common.toast.success'), result.message || t('cardGeneration.individual.success.cardGenerated', { cardId }));

                // Gérer le batch: créer un nouveau batch si aucun n'est sélectionné, sinon ajouter à l'existant
                let targetBatchId = formData.batchId;

                if (!targetBatchId) {
                    // Créer un nouveau batch avec le nom du bénéficiaire
                    const batchName = `${selectedBeneficiary.firstName || selectedBeneficiary.prenom || ''} ${selectedBeneficiary.lastName || selectedBeneficiary.nom || ''}`.trim();

                    try {
                        const batchResponse = await CarteV2Service.createCardBatchWithTemplate({
                            batchName: batchName || `Card ${cardId}`,
                            countryCode: formData.pays,
                            templateId: parseInt(formData.templateId),
                            expectedQuantity: 1,
                            plannedStartDate: new Date().toISOString(),
                            plannedEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                            createdBy: localStorage.getItem('userEmail') || 'admin@company.com',
                            cardIds: [parseInt(cardId)]
                        });

                        const batchResult = batchResponse.data || batchResponse;
                        if (batchResult && (batchResult.id || batchResult.Id)) {
                            targetBatchId = batchResult.id || batchResult.Id;
                            console.log('Batch créé:', targetBatchId);
                            showToast('info', t('cardGeneration.individual.info.batchCreated'),
                                t('cardGeneration.individual.info.batchCreatedMessage', { batchName }));
                        }
                    } catch (batchError) {
                        console.error('Erreur création batch:', batchError);
                        // Continue même si le batch échoue
                    }
                } else {
                    // Ajouter la carte au batch existant
                    try {
                        await CarteV2Service.addCardToBatch(targetBatchId, {
                            cardId: parseInt(cardId),
                            addedBy: localStorage.getItem('userEmail') || 'admin@company.com'
                        });
                        console.log('Carte ajoutée au batch:', targetBatchId);
                        showToast('info', t('cardGeneration.individual.info.cardAddedToBatch'),
                            t('cardGeneration.individual.info.cardAddedToBatchMessage'));
                    } catch (addError) {
                        console.error('Erreur ajout au batch:', addError);
                        // Continue même si l'ajout échoue
                    }
                }

                // Réinitialiser le formulaire
                setTimeout(() => {
                    resetForm();
                }, 2000);
            } else {
                // Gestion spéciale pour paiement requis
                if (result.message?.includes('Paiement requis')) {
                    showToast('warn', t('cardGeneration.individual.info.paymentRequired'),
                        t('cardGeneration.individual.info.paymentRequiredMessage'),
                        7000
                    );

                    // Rediriger vers l'onglet Duplicata après 2 secondes
                    setTimeout(() => {
                        // Trouver l'élément parent qui contient tous les onglets
                        const parentComponent = document.querySelector('[role="tablist"]');
                        if (parentComponent) {
                            // Chercher l'onglet Duplicata et le cliquer
                            const duplicataTab = Array.from(parentComponent.querySelectorAll('[role="tab"]'))
                                .find(tab => tab.textContent.includes('Duplicata') || tab.textContent.includes('Paiement'));
                            if (duplicataTab) {
                                duplicataTab.click();
                            } else {
                                // Si pas trouvé par le texte, essayer de naviguer directement
                                window.location.hash = '#duplicata';
                            }
                        }
                    }, 2000);
                } else {
                    showToast('error', t('common.toast.error'), result.message || t('cardGeneration.individual.errors.generate'));
                }
            }
        } catch (error) {
            console.error('Erreur génération:', error);
            showToast('error', t('common.toast.error'), t('cardGeneration.individual.errors.generate'));
        } finally {
            setLoading(false);
        }
    };

    // Réinitialiser le formulaire
    const resetForm = () => {
        setActiveStep(0);
        setSearchTerm('');
        setSearchResults([]);
        setSelectedBeneficiary(null);

        // Garder le pays actuellement sélectionné ou prendre le premier pays disponible
        const defaultCountry = countries.length > 0
            ? (countries[0].code || countries[0].Code)
            : '';

        setFormData({
            numeroAssure: '',
            nom: '',
            prenom: '',
            dateNaissance: null,
            numeroPolice: '',
            souscripteur: '',
            pays: defaultCountry,
            priorite: 'NORMAL',
            serialNumber: '',
            notes: '',
            templateId: null,
            stockId: null,
            batchId: null
        });
        setPhoto(null);
        setPhotoPreview(null);
        setSelectedTemplate(null);
        setPreviewUrl(null);
    };

    // Rendu de l'étape 1: Recherche
    const renderSearchStep = () => (
        <Card className="mb-4">
            <h5 className="mb-4">
                <i className="pi pi-search mr-2"></i>
                {t('cardGeneration.individual.searchTitle')}
            </h5>

            <div className="p-fluid mb-4">
                <div className="p-inputgroup">
                    <InputText
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('cardGeneration.individual.placeholders.searchBeneficiary')}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button
                        icon="pi pi-search"
                        label={t('cardGeneration.individual.buttons.search')}
                        onClick={handleSearch}
                        loading={loading}
                    />
                </div>
            </div>

            {searchResults.length > 0 && (
                <DataTable
                    value={searchResults}
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 20]}
                    emptyMessage={t('cardGeneration.individual.info.noResults')}
                >
                    <Column field="numeroAssure" header={t('cardGeneration.individual.labels.insuredNumber')} sortable body={(rowData) => rowData.idClient || rowData.IdClient || rowData.numeroAssure|| rowData.numeroAssure || rowData.id || rowData.Id || rowData.code} />
                    <Column field="lastName" header={t('cardGeneration.individual.labels.lastName')} sortable body={(rowData) => rowData.lastName || rowData.nom} />
                    <Column field="firstName" header={t('cardGeneration.individual.labels.firstName')} sortable body={(rowData) => rowData.firstName || rowData.prenom} />
                    <Column field="birthDate" header={t('cardGeneration.individual.labels.birthdate')} sortable body={(rowData) => {
                        const date = rowData.birthdate || rowData.birthDate || rowData.dateNaissance;
                        return date ? new Date(date).toLocaleDateString('fr-FR') : 'N/A';
                    }} />
                    <Column field="policeNumber" header={t('cardGeneration.individual.labels.policeNumber')} body={(rowData) => rowData.policeNumber || rowData.numeroPolice || 'N/A'} />
                    <Column header={t('cardGeneration.individual.labels.photo')} body={(rowData) => {
                        const hasPhoto = rowData.clientPhoto || rowData.photoUrl || rowData.PhotoUrl || rowData.photo || rowData.Photo || rowData.photoPath || rowData.PhotoPath;
                        return hasPhoto ? (
                            <i className="pi pi-image text-green-500" title={t('cardGeneration.individual.info.photoAvailable')}></i>
                        ) : (
                            <i className="pi pi-image text-gray-300" title={t('cardGeneration.individual.info.noPhoto')}></i>
                        );
                    }} style={{ width: '80px', textAlign: 'center' }} />
                    <Column
                        header={t('cardGeneration.individual.labels.actions') || 'Actions'}
                        body={(rowData) => (
                            <div className="flex gap-2">
                                <Button
                                    icon="pi pi-pencil"
                                    className="p-button-rounded p-button-text p-button-info"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditBeneficiary(rowData);
                                    }}
                                    tooltip={t('cardGeneration.individual.buttons.edit') || 'Modifier'}
                                    tooltipOptions={{ position: 'top' }}
                                    visible={false} // Spécialement caché pour l'instant
                                />
                                <Button
                                    icon="pi pi-arrow-right"
                                    className="p-button-rounded p-button-text p-button-success"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectBeneficiary(rowData);
                                    }}
                                    tooltip={t('cardGeneration.individual.buttons.select') || 'Sélectionner'}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        )}
                        style={{ width: '120px', textAlign: 'center' }}
                    />
                </DataTable>
            )}
        </Card>
    );

    // Rendu de l'étape 2: Informations
    const renderFormStep = () => (
        <Card className="mb-4">
            <h5 className="mb-4">
                <i className="pi pi-id-card mr-2"></i>
                {t('cardGeneration.individual.cardInformationTitle')}
            </h5>

            {selectedBeneficiary && (
                <div className="mb-4 p-3 bg-blue-50 border-round">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-user text-blue-500"></i>
                        <span className="font-semibold">
                            {t('cardGeneration.individual.labels.beneficiary')}: {selectedBeneficiary.firstName} {selectedBeneficiary.lastName}
                        </span>
                    </div>
                    {selectedBeneficiary.assurePrincipalName && (
                        <div className="flex align-items-center gap-2 ml-4">
                            <i className="pi pi-users text-blue-400"></i>
                            <span className="text-sm">
                                {t('cardGeneration.individual.labels.mainInsuredName')}: {selectedBeneficiary.assurePrincipalName}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <Row>
                <Col md={8}>
                    <div className="p-fluid">
                        <Row>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="numeroAssure" className="font-semibold">
                                        {t('cardGeneration.individual.labels.insuredNumber')} *
                                    </label>
                                    <InputText
                                        id="numeroAssure"
                                        value={formData.numeroAssure}
                                        onChange={(e) => setFormData({ ...formData, numeroAssure: e.target.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.insuredNumber')}
                                        disabled true
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="serialNumber" className="font-semibold">
                                        {t('cardGeneration.individual.labels.serialNumber')}
                                    </label>
                                    <InputText
                                        id="serialNumber"
                                        value={formData.serialNumber}
                                        onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.serialNumber')}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="nom" className="font-semibold">{t('cardGeneration.individual.labels.lastName')} *</label>
                                    <InputText
                                        id="nom"
                                        value={formData.nom}
                                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.lastName')}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="prenom" className="font-semibold">{t('cardGeneration.individual.labels.firstName')} *</label>
                                    <InputText
                                        id="prenom"
                                        value={formData.prenom}
                                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.firstName')}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="dateNaissance" className="font-semibold">
                                        {t('cardGeneration.individual.labels.birthdate')}
                                    </label>
                                    <Calendar
                                        id="dateNaissance"
                                        value={formData.dateNaissance}
                                        onChange={(e) => setFormData({ ...formData, dateNaissance: e.value })}
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        placeholder={t('cardGeneration.individual.placeholders.birthdate')}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="numeroPolice" className="font-semibold">
                                        {t('cardGeneration.individual.labels.policeNumber')}
                                    </label>
                                    <InputText
                                        id="numeroPolice"
                                        value={formData.numeroPolice}
                                        onChange={(e) => setFormData({ ...formData, numeroPolice: e.target.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.policeNumber')}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <div className="field mb-3">
                                    <label htmlFor="souscripteur" className="font-semibold">
                                        {t('cardGeneration.individual.labels.subscriber') || 'Souscripteur'}
                                    </label>
                                    <InputText
                                        id="souscripteur"
                                        value={formData.souscripteur}
                                        onChange={(e) => setFormData({ ...formData, souscripteur: e.target.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.subscriber') || 'Nom du souscripteur'}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="pays" className="font-semibold">{t('cardGeneration.individual.labels.country')} *</label>
                                    <Dropdown
                                        id="pays"
                                        value={formData.pays}
                                        options={countryOptions}
                                        onChange={(e) => setFormData({ ...formData, pays: e.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.selectCountry')}
                                        filter
                                        emptyMessage={t('cardGeneration.individual.info.noCountryAvailable') || 'Aucun pays disponible'}
                                        disabled={countriesLoading || countries.length === 0}
                                    />
                                    {countriesLoading && (
                                        <small className="text-muted d-block mt-2">
                                            {t('cardGeneration.individual.info.loadingCountries') || 'Chargement des pays...'}
                                        </small>
                                    )}
                                    {countries.length === 0 && !countriesLoading && (
                                        <small className="text-danger d-block mt-2">
                                            {t('cardGeneration.individual.info.noCountryMessage') || 'Aucun pays disponible'}
                                        </small>
                                    )}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="priorite" className="font-semibold">{t('cardGeneration.individual.labels.priority')}</label>
                                    <Dropdown
                                        id="priorite"
                                        value={formData.priorite}
                                        options={priorityOptions}
                                        onChange={(e) => setFormData({ ...formData, priorite: e.value })}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <div className="field mb-3">
                                    <label htmlFor="template" className="font-semibold">
                                        {t('cardGeneration.individual.labels.cardTemplate')} *
                                    </label>
                                    <Dropdown
                                        id="template"
                                        value={formData.templateId}
                                        options={templates.map(template => {
                                            const templateId = template.id || template.Id;
                                            const templateName = template.templateName || template.TemplateName ||
                                                               template.name || template.Name || `Template ${templateId}`;
                                            const isDefault = template.isDefault || template.IsDefault;
                                            return {
                                                label: `${templateName}${isDefault ? ` (${t('cardGeneration.individual.labels.default')})` : ''}`,
                                                value: templateId
                                            };
                                        })}
                                        onChange={handleTemplateChange}
                                        placeholder={t('cardGeneration.individual.placeholders.selectTemplate')}
                                        filter
                                        emptyMessage={t('cardGeneration.individual.info.noTemplateAvailable')}
                                        disabled={loading || templates.length === 0}
                                    />
                                    {selectedTemplate && (
                                        <div className="mt-2 p-2 bg-blue-50 border-round">
                                            <small className="text-sm">
                                                <strong>{t('cardGeneration.individual.labels.country')}:</strong> {selectedTemplate.countryCode || selectedTemplate.CountryCode} |
                                                <strong> {t('cardGeneration.individual.labels.name')}:</strong> {selectedTemplate.templateName || selectedTemplate.TemplateName ||
                                                                       selectedTemplate.name || selectedTemplate.Name || t('cardGeneration.individual.labels.standardTemplate')}
                                            </small>
                                        </div>
                                    )}
                                    {templates.length === 0 && !loading && (
                                        <small className="text-danger d-block mt-2">
                                            {t('cardGeneration.individual.info.noTemplateForCountry', { country: formData.pays })}
                                        </small>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <div className="field mb-3">
                                    <label htmlFor="stock" className="font-semibold">
                                        {t('cardGeneration.individual.labels.cardStock')} *
                                    </label>
                                    <Dropdown
                                        id="stock"
                                        value={formData.stockId}
                                        options={stocks.map(s => {
                                            const stockId = s.id || s.Id;
                                            const stockName = s.productName || s.ProductName || `Stock ${stockId}`;
                                            const available = s.currentQuantity || s.CurrentQuantity ||
                                                             s.quantityAvailable || s.QuantityAvailable || 0;
                                            return {
                                                label: `${stockName} (${available} ${t('cardGeneration.individual.labels.available')})`,
                                                value: stockId
                                            };
                                        })}
                                        onChange={(e) => setFormData({ ...formData, stockId: e.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.selectStock')}
                                        filter
                                        emptyMessage={t('cardGeneration.individual.info.noStockAvailable')}
                                        disabled={stocksLoading || stocks.length === 0}
                                    />
                                    {stocksLoading && (
                                        <small className="text-muted d-block mt-2">
                                            {t('cardGeneration.individual.info.loadingStocks')}
                                        </small>
                                    )}
                                    {stocks.length === 0 && !stocksLoading && (
                                        <small className="text-danger d-block mt-2">
                                            {t('cardGeneration.individual.info.noStockMessage')}
                                        </small>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <div className="field mb-3">
                                    <label htmlFor="batch" className="font-semibold">
                                        {t('cardGeneration.individual.labels.cardBatch')}
                                    </label>
                                    <Dropdown
                                        id="batch"
                                        value={formData.batchId}
                                        options={batches.map(b => {
                                            const batchId = b.id || b.Id;
                                            const batchNumber = b.batchNumber || b.BatchNumber || `Batch ${batchId}`;
                                            const totalCards = b.totalCards || b.TotalCards || 0;
                                            return {
                                                label: `${batchNumber} (${totalCards} ${t('cardGeneration.individual.labels.cards')})`,
                                                value: batchId
                                            };
                                        })}
                                        onChange={(e) => setFormData({ ...formData, batchId: e.value })}
                                        placeholder={t('cardGeneration.individual.placeholders.selectBatch')}
                                        filter
                                        showClear
                                        emptyMessage={t('cardGeneration.individual.info.noBatchAvailable')}
                                        disabled={batchesLoading}
                                    />
                                    {batchesLoading && (
                                        <small className="text-muted d-block mt-2">
                                            {t('cardGeneration.individual.info.loadingBatches')}
                                        </small>
                                    )}
                                    <small className="text-muted d-block mt-2">
                                        {t('cardGeneration.individual.info.batchOptional')}
                                    </small>
                                </div>
                            </Col>
                        </Row>

                        <div className="field mb-3">
                            <label htmlFor="notes" className="font-semibold">{t('cardGeneration.individual.labels.notes')}</label>
                            <InputTextarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                placeholder={t('cardGeneration.individual.placeholders.notes')}
                            />
                        </div>
                    </div>
                </Col>

                <Col md={4}>
                    <Panel header={t('cardGeneration.individual.labels.beneficiaryPhoto')}>
                        <div className="text-center">
                            {photoPreview ? (
                                <div className="mb-3">
                                    <img
                                        src={photoPreview}
                                        alt={t('cardGeneration.individual.labels.photo')}
                                        style={{
                                            width: '100%',
                                            maxWidth: '200px',
                                            height: '240px',
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            border: '2px solid #dee2e6'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-rounded p-button-danger p-button-sm mt-2"
                                        onClick={() => {
                                            if (photoPreview) {
                                                URL.revokeObjectURL(photoPreview);
                                            }
                                            setPhoto(null);
                                            setPhotoPreview(null);
                                        }}
                                        tooltip={t('cardGeneration.individual.buttons.deletePhoto')}
                                    />
                                </div>
                            ) : (
                                <div className="mb-3 p-4 border-round bg-gray-100">
                                    <i className="pi pi-user text-6xl text-gray-400"></i>
                                    <p className="text-sm text-gray-600 mt-2">{t('cardGeneration.individual.info.noPhoto')}</p>
                                </div>
                            )}

                            <FileUpload
                                mode="basic"
                                accept="image/*"
                                maxFileSize={5000000}
                                customUpload
                                uploadHandler={onPhotoSelect}
                                auto
                                chooseLabel={photoPreview ? t('cardGeneration.individual.buttons.changePhoto') : t('cardGeneration.individual.buttons.addPhoto')}
                                className="w-full"
                            />
                            <small className="text-muted">{t('cardGeneration.individual.info.photoFormat')}</small>
                        </div>
                    </Panel>
                </Col>
            </Row>

            <Divider />

            <div className="flex justify-content-between mt-4">
                <Button
                    label={t('cardGeneration.individual.buttons.back')}
                    icon="pi pi-arrow-left"
                    className="p-button-secondary"
                    onClick={() => setActiveStep(0)}
                />
                <div className="flex gap-2">
                    <Button
                        label={t('cardGeneration.individual.buttons.preview')}
                        icon="pi pi-eye"
                        className="p-button-outlined"
                        onClick={handlePreview}
                        disabled={!formData.templateId || !formData.numeroAssure || !formData.nom || !formData.prenom}
                    />
                    <Button
                        label={t('cardGeneration.individual.buttons.next')}
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        onClick={() => setActiveStep(2)}
                        disabled={!formData.numeroAssure || !formData.nom || !formData.prenom || !formData.templateId || !formData.stockId}
                    />
                </div>
            </div>
        </Card>
    );

    // Rendu de l'étape 3: Confirmation
    const renderConfirmStep = () => (
        <Card className="mb-4">
            <h5 className="mb-4">
                <i className="pi pi-check-circle mr-2"></i>
                {t('cardGeneration.individual.confirmationTitle')}
            </h5>

            <Panel header={t('cardGeneration.individual.labels.summary')} className="mb-4">
                <div className="confirmation-details">
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <strong>{t('cardGeneration.individual.labels.insuredNumber')}:</strong>
                                <div className="mt-1">{formData.numeroAssure}</div>
                            </div>
                            <div className="mb-3">
                                <strong>{t('cardGeneration.individual.labels.fullName')}:</strong>
                                <div className="mt-1">{formData.prenom} {formData.nom}</div>
                            </div>
                            <div className="mb-3">
                                <strong>{t('cardGeneration.individual.labels.birthdate')}:</strong>
                                <div className="mt-1">
                                    {formData.dateNaissance ? formData.dateNaissance.toLocaleDateString('fr-FR') : t('cardGeneration.individual.info.notProvided')}
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <strong>{t('cardGeneration.individual.labels.policeNumber')}:</strong>
                                <div className="mt-1">{formData.numeroPolice || t('cardGeneration.individual.info.notProvided')}</div>
                            </div>
                            <div className="mb-3">
                                <strong>{t('cardGeneration.individual.labels.country')}:</strong>
                                <div className="mt-1">
                                    <Tag value={countryOptions.find(c => c.value === formData.pays)?.label || t('cardGeneration.individual.info.notDefined')} />
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>{t('cardGeneration.individual.labels.priority')}:</strong>
                                <div className="mt-1">
                                    <Tag
                                        value={priorityOptions.find(p => p.value === formData.priorite)?.label || t('cardGeneration.individual.priority.normal')}
                                        severity={formData.priorite === 'URGENT' ? 'danger' : formData.priorite === 'HIGH' ? 'warning' : 'info'}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {formData.serialNumber && (
                        <div className="mb-3">
                            <strong>{t('cardGeneration.individual.labels.serialNumber')}:</strong>
                            <div className="mt-1">{formData.serialNumber}</div>
                        </div>
                    )}

                    {formData.souscripteur && (
                        <div className="mb-3">
                            <strong>{t('cardGeneration.individual.labels.subscriber') || 'Souscripteur'}:</strong>
                            <div className="mt-1">{formData.souscripteur}</div>
                        </div>
                    )}

                    {selectedTemplate && (
                        <div className="mb-3">
                            <strong>{t('cardGeneration.individual.labels.template')}:</strong>
                            <div className="mt-1">
                                <Tag value={selectedTemplate.name} severity="info" />
                            </div>
                        </div>
                    )}

                    {formData.notes && (
                        <div className="mb-3">
                            <strong>{t('cardGeneration.individual.labels.notes')}:</strong>
                            <div className="mt-1">{formData.notes}</div>
                        </div>
                    )}

                    {photoPreview && (
                        <div className="mb-3">
                            <strong>{t('cardGeneration.individual.labels.photo')}:</strong>
                            <div className="mt-2">
                                <img
                                    src={photoPreview}
                                    alt={t('cardGeneration.individual.labels.preview')}
                                    style={{
                                        width: '150px',
                                        height: '180px',
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Panel>

            {/* Prévisualisation de la carte si disponible */}
            {previewUrl && (
                <Panel header={t('cardGeneration.individual.labels.cardPreview')} className="mb-4">
                    <div className="text-center">
                        <img
                            src={previewUrl}
                            alt={t('cardGeneration.individual.labels.cardPreview')}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                        />
                    </div>
                </Panel>
            )}

            <div className="p-3 bg-yellow-50 border-round mb-4">
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle text-yellow-600 text-2xl mr-3"></i>
                    <div>
                        <strong>{t('common.toast.warning')}</strong>
                        <p className="mb-0 mt-1">
                            {t('cardGeneration.individual.info.verifyBeforeGenerate')}
                        </p>
                    </div>
                </div>
            </div>

            <Divider />

            <div className="flex justify-content-between mt-4">
                <Button
                    label={t('cardGeneration.individual.buttons.back')}
                    icon="pi pi-arrow-left"
                    className="p-button-secondary"
                    onClick={() => setActiveStep(1)}
                />
                <Button
                    label={t('cardGeneration.individual.buttons.generateCard')}
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={handleGenerate}
                    loading={loading}
                />
            </div>
        </Card>
    );

    return (
        <div className="generation-individuelle">
            <Toast ref={toast} />

            <div className="mb-4">
                <div className="flex justify-content-between align-items-start">
                    <div>
                        <h4 className="mb-2">
                            <i className="pi pi-id-card mr-2"></i>
                            {t('cardGeneration.individual.title')}
                        </h4>
                        <p className="text-muted mb-0">{t('cardGeneration.individual.subtitle')}</p>
                    </div>
                    <Button
                        label={t('cardGeneration.individual.buttons.newBeneficiary')}
                        icon="pi pi-user-plus"
                        className="p-button-success"
                        onClick={() => {
                            setShowNewBeneficiaryDialog(true);
                            loadPrincipalBeneficiaries(); // Charger les assurés principaux
                        }}
                    />
                </div>
            </div>

            <Steps
                model={steps}
                activeIndex={activeStep}
                onSelect={(e) => {
                    // Permettre la navigation uniquement vers les étapes accessibles
                    if (e.index === 0 || (e.index === 1 && selectedBeneficiary) || (e.index === 2 && selectedBeneficiary && formData.nom && formData.prenom)) {
                        setActiveStep(e.index);
                    }
                }}
                readOnly={false}
                className="mb-4"
            />

            {activeStep === 0 && renderSearchStep()}
            {activeStep === 1 && renderFormStep()}
            {activeStep === 2 && renderConfirmStep()}

            {loading && (
                <div className="flex justify-content-center mt-4">
                    <ProgressSpinner />
                </div>
            )}

            {/* Dialog de prévisualisation */}
            <Dialog
                visible={showPreviewDialog}
                onHide={() => setShowPreviewDialog(false)}
                header={t('cardGeneration.individual.labels.cardPreview')}
                style={{ width: '90vw', maxWidth: '1200px' }}
                modal
            >
                <div className="p-4">
                    <Row className="justify-content-center">
                        {/* Face avant */}
                        <Col md={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-3 font-semibold">
                                    {t('cardGeneration.individual.labels.front') || 'Avant (Front)'}
                                </h6>
                                {frontPreviewUrl ? (
                                    <img
                                        src={frontPreviewUrl}
                                        alt={t('cardGeneration.individual.labels.front')}
                                        style={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }}
                                    />
                                ) : (
                                    <div className="flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                        <ProgressSpinner />
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Face arrière */}
                        <Col md={6}>
                            <div className="text-center">
                                <h6 className="mb-3 font-semibold">
                                    {t('cardGeneration.individual.labels.back') || 'Arrière (Back)'}
                                </h6>
                                {backPreviewUrl ? (
                                    <img
                                        src={backPreviewUrl}
                                        alt={t('cardGeneration.individual.labels.back')}
                                        style={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }}
                                    />
                                ) : (
                                    <div className="flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                        <ProgressSpinner />
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>

                <div className="flex justify-content-center gap-2 mt-3">
                    <Button
                        label={t('cardGeneration.individual.buttons.print') || 'Imprimer'}
                        icon="pi pi-print"
                        className="p-button-success"
                        onClick={handlePrintCard}
                        disabled={(!frontPreviewUrl && !backPreviewUrl) || loading}
                    />
                    <Button
                        label={t('cardGeneration.individual.buttons.download') || 'Télécharger'}
                        icon="pi pi-download"
                        className="p-button-primary"
                        onClick={handleDownloadCardImage}
                        disabled={(!frontPreviewUrl && !backPreviewUrl) || loading}
                    />
                    <Button
                        label={t('cardGeneration.individual.buttons.close')}
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={() => setShowPreviewDialog(false)}
                    />
                </div>
            </Dialog>

            {/* Dialog nouveau bénéficiaire */}
            <Dialog
                visible={showNewBeneficiaryDialog}
                onHide={() => setShowNewBeneficiaryDialog(false)}
                header={t('cardGeneration.individual.dialogs.createNewBeneficiary')}
                style={{ width: '750px' }}
                modal
                footer={
                    <div>
                        <Button
                            label={t('cardGeneration.individual.buttons.cancel')}
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setShowNewBeneficiaryDialog(false);
                                // Nettoyer la photo
                                if (newBeneficiaryPhotoPreview) {
                                    URL.revokeObjectURL(newBeneficiaryPhotoPreview);
                                }
                                setNewBeneficiaryPhoto(null);
                                setNewBeneficiaryPhotoPreview(null);
                            }}
                            disabled={loading}
                        />
                        <Button
                            label={t('cardGeneration.individual.buttons.create')}
                            icon="pi pi-check"
                            className="p-button-success"
                            onClick={handleCreateBeneficiary}
                            loading={loading}
                        />
                    </div>
                }
            >
                <div className="p-fluid">
                    <Row>
                        <Col md={8}>
                            <Row>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newFirstName" className="font-semibold">
                                            {t('cardGeneration.individual.labels.firstName')} *
                                        </label>
                                        <InputText
                                            id="newFirstName"
                                            value={newBeneficiaryData.firstName}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, firstName: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.firstName')}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newLastName" className="font-semibold">
                                            {t('cardGeneration.individual.labels.lastName')} *
                                        </label>
                                        <InputText
                                            id="newLastName"
                                            value={newBeneficiaryData.lastName}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, lastName: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.lastName')}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newBirthDate" className="font-semibold">
                                            {t('cardGeneration.individual.labels.birthdate')} *
                                        </label>
                                        <Calendar
                                            id="newBirthDate"
                                            value={newBeneficiaryData.birthdate}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, birthdate: e.value })}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            placeholder={t('cardGeneration.individual.placeholders.birthdate')}
                                            maxDate={new Date()}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newGender" className="font-semibold">
                                            {t('cardGeneration.individual.labels.gender')} *
                                        </label>
                                        <Dropdown
                                            id="newGender"
                                            value={newBeneficiaryData.sexe}
                                            options={genderOptions}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, sexe: e.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.selectGender')}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newPoliceNumber" className="font-semibold">
                                            {t('cardGeneration.individual.labels.policeNumber')} *
                                        </label>
                                        <InputText
                                            id="newPoliceNumber"
                                            value={newBeneficiaryData.numeroPolice}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, numeroPolice: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.policeNumber')}
                                        />
                                    </div>
                                </Col>
                                {/* <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newNumeroAssurer" className="font-semibold">
                                            {t('cardGeneration.individual.labels.insuredNumber')}
                                        </label>
                                        <InputText
                                            id="newNumeroAssurer"
                                            value={newBeneficiaryData.numeroAssurer}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, numeroAssurer: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.insuredNumber') + ' (optionnal)'}
                                            keyfilter="int"
                                        />
                                        <small className="text-muted d-block mt-1">
                                            {t('cardGeneration.individual.info.autoGeneratedIfEmpty') || 'Généré automatiquement si non fourni'}
                                        </small>
                                    </div>
                                </Col> */}
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newEmail" className="font-semibold">
                                            {t('cardGeneration.individual.labels.email')}
                                        </label>
                                        <InputText
                                            id="newEmail"
                                            type="email"
                                            value={newBeneficiaryData.email}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, email: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.email')}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="newPhone" className="font-semibold">
                                            {t('cardGeneration.individual.labels.phone')}
                                        </label>
                                        <InputText
                                            id="newPhone"
                                            value={newBeneficiaryData.phoneNumber}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, phoneNumber: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.phone')}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="subscriberName" className="font-semibold">
                                            {t('cardGeneration.individual.labels.subscriberName')} *
                                        </label>
                                        <InputText
                                            id="subscriberName"
                                            value={newBeneficiaryData.subscriberName}
                                            onChange={(e) => setNewBeneficiaryData({ ...newBeneficiaryData, subscriberName: e.target.value })}
                                            placeholder={t('cardGeneration.individual.placeholders.subscriberName')}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="field mb-3">
                                        <label htmlFor="mainInsuredName" className="font-semibold">
                                            {t('cardGeneration.individual.labels.mainInsuredName')} {newBeneficiaryData.relationshipWithMainInsured !== 'PRINCIPAL' && '*'}
                                        </label>
                                        <Dropdown
                                            id="mainInsuredName"
                                            value={newBeneficiaryData.mainInsuredName}
                                            options={principalBeneficiaries.map(pb => {
                                                const firstName = pb.firstName || '';
                                                const lastName = pb.lastName || '';
                                                const numeroAssure = pb.numeroAssure || pb.numeroAssure|| pb.idClient || '';
                                                const fullName = `${firstName} ${lastName}`.trim();
                                                return {
                                                    label: numeroAssure ? `${fullName} (${numeroAssure})` : fullName,
                                                    value: fullName,
                                                    numeroAssure: numeroAssure
                                                };
                                            })}
                                            onChange={(e) => {
                                                const selectedOption = principalBeneficiaries
                                                    .map(pb => {
                                                        const firstName = pb.firstName || '';
                                                        const lastName = pb.lastName || '';
                                                        const numeroAssure = pb.numeroAssure || pb.numeroAssure|| pb.idClient || '';
                                                        const fullName = `${firstName} ${lastName}`.trim();
                                                        return { fullName, numeroAssure };
                                                    })
                                                    .find(opt => opt.fullName === e.value);

                                                setNewBeneficiaryData({
                                                    ...newBeneficiaryData,
                                                    mainInsuredName: e.value,
                                                    mainInsuredNumeroAssurer: selectedOption?.numeroAssure || ''
                                                });
                                            }}
                                            placeholder={principalBeneficiariesLoading
                                                ? t('common.await-text')
                                                : t('cardGeneration.individual.placeholders.mainInsuredName')}
                                            filter
                                            showClear
                                            disabled={principalBeneficiariesLoading || newBeneficiaryData.relationshipWithMainInsured === 'PRINCIPAL'}
                                            emptyMessage={principalBeneficiariesLoading
                                                ? t('cardGeneration.individual.labels.loadingPrincipalBeneficiaries')
                                                : t('cardGeneration.individual.labels.noPrincipalBeneficiaryFound')}
                                        />
                                        {newBeneficiaryData.relationshipWithMainInsured === 'PRINCIPAL' && (
                                            <small className="text-muted d-block mt-1">
                                                {t('cardGeneration.individual.labels.autoFilledWithBeneficiaryName')}
                                            </small>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <div className="field mb-3">
                                        <label htmlFor="relationshipWithMainInsured" className="font-semibold">
                                            {t('cardGeneration.individual.labels.relationshipWithMainInsured')} *
                                        </label>
                                        <Dropdown
                                            id="relationshipWithMainInsured"
                                            value={newBeneficiaryData.relationshipWithMainInsured}
                                            options={relationshipOptions}
                                            onChange={(e) => {
                                                const newRelationship = e.value;
                                                // Si c'est l'assuré principal (PRINCIPAL), auto-remplir avec son nom complet et son numéro
                                                if (newRelationship === 'PRINCIPAL') {
                                                    const fullName = `${newBeneficiaryData.firstName.trim()} ${newBeneficiaryData.lastName.trim()}`.trim();
                                                    setNewBeneficiaryData({
                                                        ...newBeneficiaryData,
                                                        relationshipWithMainInsured: newRelationship,
                                                        mainInsuredName: fullName || newBeneficiaryData.mainInsuredName,
                                                        mainInsuredNumeroAssurer: newBeneficiaryData.numeroAssure|| '' // Utiliser son propre numéro
                                                    });
                                                } else {
                                                    setNewBeneficiaryData({ ...newBeneficiaryData, relationshipWithMainInsured: newRelationship });
                                                }
                                            }}
                                            placeholder={t('cardGeneration.individual.placeholders.selectRelationship')}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Col>

                        <Col md={4}>
                            <div className="field mb-3">
                                <label className="font-semibold">{t('cardGeneration.individual.labels.photo')}</label>
                                <div className="text-center">
                                    {newBeneficiaryPhotoPreview ? (
                                        <div className="mb-3">
                                            <img
                                                src={newBeneficiaryPhotoPreview}
                                                alt={t('cardGeneration.individual.labels.photo')}
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '150px',
                                                    height: '180px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '2px solid #dee2e6'
                                                }}
                                            />
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-danger p-button-sm mt-2"
                                                onClick={() => {
                                                    if (newBeneficiaryPhotoPreview) {
                                                        URL.revokeObjectURL(newBeneficiaryPhotoPreview);
                                                    }
                                                    setNewBeneficiaryPhoto(null);
                                                    setNewBeneficiaryPhotoPreview(null);
                                                    setNewBeneficiaryData({ ...newBeneficiaryData, clientPhoto: null });
                                                }}
                                                tooltip={t('cardGeneration.individual.buttons.deletePhoto')}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mb-3 p-4 border-round bg-gray-100">
                                            <i className="pi pi-user text-6xl text-gray-400"></i>
                                            <p className="text-sm text-gray-600 mt-2">{t('cardGeneration.individual.info.noPhoto')}</p>
                                        </div>
                                    )}

                                    <FileUpload
                                        mode="basic"
                                        accept="image/*"
                                        maxFileSize={5000000}
                                        customUpload
                                        uploadHandler={onNewBeneficiaryPhotoSelect}
                                        auto
                                        chooseLabel={newBeneficiaryPhotoPreview ? t('cardGeneration.individual.buttons.change') : t('cardGeneration.individual.buttons.add')}
                                        className="w-full"
                                    />
                                    <small className="text-muted d-block mt-1">{t('cardGeneration.individual.info.maxSize')} 5M</small>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div className="mb-3 p-3 bg-blue-50 border-round">
                        <small className="text-sm">
                            <i className="pi pi-info-circle mr-2"></i>
                            {t('cardGeneration.individual.info.requiredFieldsNote')}
                        </small>
                    </div>
                </div>
            </Dialog>

            {/* Dialog édition bénéficiaire */}
            <Dialog
                visible={showEditBeneficiaryDialog}
                onHide={() => {
                    setShowEditBeneficiaryDialog(false);
                    setEditBeneficiaryData(null);
                    if (editBeneficiaryPhotoPreview && editBeneficiaryPhotoPreview.startsWith('blob:')) {
                        URL.revokeObjectURL(editBeneficiaryPhotoPreview);
                    }
                    setEditBeneficiaryPhoto(null);
                    setEditBeneficiaryPhotoPreview(null);
                }}
                header={t('cardGeneration.individual.dialogs.editBeneficiary') || 'Modifier le bénéficiaire'}
                style={{ width: '750px' }}
                modal
                footer={
                    <div>
                        <Button
                            label={t('cardGeneration.individual.buttons.cancel')}
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setShowEditBeneficiaryDialog(false);
                                setEditBeneficiaryData(null);
                                if (editBeneficiaryPhotoPreview && editBeneficiaryPhotoPreview.startsWith('blob:')) {
                                    URL.revokeObjectURL(editBeneficiaryPhotoPreview);
                                }
                                setEditBeneficiaryPhoto(null);
                                setEditBeneficiaryPhotoPreview(null);
                            }}
                            disabled={loading}
                        />
                        <Button
                            label={t('cardGeneration.individual.buttons.update') || 'Mettre à jour'}
                            icon="pi pi-check"
                            className="p-button-success"
                            onClick={handleUpdateBeneficiary}
                            loading={loading}
                        />
                    </div>
                }
            >
                {editBeneficiaryData && (
                    <div className="p-fluid">
                        <Row>
                            <Col md={8}>
                                <Row>
                                    <Col md={6}>
                                        <div className="field mb-3">
                                            <label htmlFor="editFirstName" className="font-semibold">
                                                {t('cardGeneration.individual.labels.firstName')} *
                                            </label>
                                            <InputText
                                                id="editFirstName"
                                                value={editBeneficiaryData.firstName}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, firstName: e.target.value })}
                                                placeholder={t('cardGeneration.individual.placeholders.firstName')}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="field mb-3">
                                            <label htmlFor="editLastName" className="font-semibold">
                                                {t('cardGeneration.individual.labels.lastName')} *
                                            </label>
                                            <InputText
                                                id="editLastName"
                                                value={editBeneficiaryData.lastName}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, lastName: e.target.value })}
                                                placeholder={t('cardGeneration.individual.placeholders.lastName')}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <div className="field mb-3">
                                            <label htmlFor="editBirthDate" className="font-semibold">
                                                {t('cardGeneration.individual.labels.birthdate')} *
                                            </label>
                                            <Calendar
                                                id="editBirthDate"
                                                value={editBeneficiaryData.birthdate}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, birthdate: e.value })}
                                                dateFormat="dd/mm/yy"
                                                showIcon
                                                placeholder={t('cardGeneration.individual.placeholders.birthdate')}
                                                maxDate={new Date()}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="field mb-3">
                                            <label htmlFor="editGender" className="font-semibold">
                                                {t('cardGeneration.individual.labels.gender')} *
                                            </label>
                                            <Dropdown
                                                id="editGender"
                                                value={editBeneficiaryData.sexe}
                                                options={genderOptions}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, sexe: e.value })}
                                                placeholder={t('cardGeneration.individual.placeholders.selectGender')}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <div className="field mb-3">
                                            <label htmlFor="editEmail" className="font-semibold">
                                                {t('cardGeneration.individual.labels.email')}
                                            </label>
                                            <InputText
                                                id="editEmail"
                                                type="email"
                                                value={editBeneficiaryData.email}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, email: e.target.value })}
                                                placeholder={t('cardGeneration.individual.placeholders.email')}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <div className="field mb-3">
                                            <label htmlFor="editPoliceNumber" className="font-semibold">
                                                {t('cardGeneration.individual.labels.policeNumber')} *
                                            </label>
                                            <InputText
                                                id="editPoliceNumber"
                                                value={editBeneficiaryData.numeroPolice}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, numeroPolice: e.target.value })}
                                                placeholder={t('cardGeneration.individual.placeholders.policeNumber')}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <div className="field mb-3">
                                            <label htmlFor="editSubscriberName" className="font-semibold">
                                                {t('cardGeneration.individual.labels.subscriberName')} *
                                            </label>
                                            <InputText
                                                id="editSubscriberName"
                                                value={editBeneficiaryData.subscriberName}
                                                onChange={(e) => setEditBeneficiaryData({ ...editBeneficiaryData, subscriberName: e.target.value })}
                                                placeholder={t('cardGeneration.individual.placeholders.subscriberName')}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <div className="field mb-3">
                                            <label htmlFor="editMainInsuredName" className="font-semibold">
                                                {t('cardGeneration.individual.labels.mainInsuredName')}
                                                {editBeneficiaryData.relationshipWithMainInsured !== 'PRINCIPAL' && ' *'}
                                            </label>
                                            <Dropdown
                                                id="editMainInsuredName"
                                                value={editBeneficiaryData.mainInsuredName}
                                                options={principalBeneficiaries.map(pb => {
                                                    const firstName = pb.firstName || '';
                                                    const lastName = pb.lastName || '';
                                                    const numeroAssure = pb.numeroAssure || pb.numeroAssure|| pb.idClient || '';
                                                    const fullName = `${firstName} ${lastName}`.trim();
                                                    return {
                                                        label: fullName,
                                                        value: fullName,
                                                        numeroAssure: numeroAssure
                                                    };
                                                })}
                                                onChange={(e) => {
                                                    const selectedOption = principalBeneficiaries
                                                        .map(pb => {
                                                            const firstName = pb.firstName || '';
                                                            const lastName = pb.lastName || '';
                                                            const numeroAssure = pb.numeroAssure || pb.numeroAssure|| pb.idClient || '';
                                                            const fullName = `${firstName} ${lastName}`.trim();
                                                            return { fullName, numeroAssure };
                                                        })
                                                        .find(opt => opt.fullName === e.value);

                                                    setEditBeneficiaryData({
                                                        ...editBeneficiaryData,
                                                        mainInsuredName: e.value,
                                                        mainInsuredNumeroAssurer: selectedOption?.numeroAssure || ''
                                                    });
                                                }}
                                                placeholder={principalBeneficiariesLoading
                                                    ? t('common.await-text')
                                                    : t('cardGeneration.individual.placeholders.mainInsuredName')}
                                                filter
                                                showClear
                                                disabled={principalBeneficiariesLoading || editBeneficiaryData.relationshipWithMainInsured === 'PRINCIPAL'}
                                                emptyMessage={principalBeneficiariesLoading
                                                    ? t('cardGeneration.individual.labels.loadingPrincipalBeneficiaries')
                                                    : t('cardGeneration.individual.labels.noPrincipalBeneficiaryFound')}
                                            />
                                            {editBeneficiaryData.relationshipWithMainInsured === 'PRINCIPAL' && (
                                                <small className="text-muted d-block mt-1">
                                                    {t('cardGeneration.individual.labels.autoFilledWithBeneficiaryName')}
                                                </small>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <div className="field mb-3">
                                            <label htmlFor="editRelationshipWithMainInsured" className="font-semibold">
                                                {t('cardGeneration.individual.labels.relationshipWithMainInsured')} *
                                            </label>
                                            <Dropdown
                                                id="editRelationshipWithMainInsured"
                                                value={editBeneficiaryData.relationshipWithMainInsured}
                                                options={relationshipOptions}
                                                onChange={(e) => {
                                                    const updates = {
                                                        ...editBeneficiaryData,
                                                        relationshipWithMainInsured: e.value
                                                    };

                                                    if (e.value === 'PRINCIPAL') {
                                                        const fullName = `${editBeneficiaryData.firstName} ${editBeneficiaryData.lastName}`.trim();
                                                        updates.mainInsuredName = fullName;
                                                    }

                                                    setEditBeneficiaryData(updates);
                                                }}
                                                placeholder={t('cardGeneration.individual.placeholders.relationshipWithMainInsured')}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>

                            <Col md={4}>
                                <div className="text-center">
                                    <label className="font-semibold d-block mb-2">
                                        {t('cardGeneration.individual.labels.photo')}
                                    </label>
                                    {editBeneficiaryPhotoPreview ? (
                                        <div className="mb-3">
                                            <img
                                                src={editBeneficiaryPhotoPreview}
                                                alt={t('cardGeneration.individual.labels.photo')}
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '200px',
                                                    height: '240px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '2px solid #dee2e6'
                                                }}
                                            />
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-danger p-button-sm mt-2"
                                                onClick={() => {
                                                    if (editBeneficiaryPhotoPreview && editBeneficiaryPhotoPreview.startsWith('blob:')) {
                                                        URL.revokeObjectURL(editBeneficiaryPhotoPreview);
                                                    }
                                                    setEditBeneficiaryPhoto(null);
                                                    setEditBeneficiaryPhotoPreview(null);
                                                    setEditBeneficiaryData({ ...editBeneficiaryData, clientPhoto: null });
                                                }}
                                                tooltip={t('cardGeneration.individual.buttons.deletePhoto')}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mb-3 p-4 border-round bg-gray-100">
                                            <i className="pi pi-user text-6xl text-gray-400"></i>
                                            <p className="text-sm text-gray-600 mt-2">{t('cardGeneration.individual.info.noPhoto')}</p>
                                        </div>
                                    )}

                                    <FileUpload
                                        mode="basic"
                                        accept="image/*"
                                        maxFileSize={5000000}
                                        customUpload
                                        uploadHandler={onEditBeneficiaryPhotoSelect}
                                        auto
                                        chooseLabel={editBeneficiaryPhotoPreview ? t('cardGeneration.individual.buttons.change') : t('cardGeneration.individual.buttons.add')}
                                        className="w-full"
                                    />
                                    <small className="text-muted d-block mt-1">{t('cardGeneration.individual.info.maxSize')} 5MB</small>
                                </div>
                            </Col>
                        </Row>

                        <div className="mb-3 p-3 bg-blue-50 border-round">
                            <small className="text-sm">
                                <i className="pi pi-info-circle mr-2"></i>
                                {t('cardGeneration.individual.info.requiredFieldsNote')}
                            </small>
                        </div>
                    </div>
                )}
            </Dialog>

            <style>{`
                .generation-individuelle .cursor-pointer tbody tr {
                    cursor: pointer;
                }

                .generation-individuelle .cursor-pointer tbody tr:hover {
                    background-color: #f8f9fa;
                }

                .generation-individuelle .field label {
                    display: block;
                    margin-bottom: 0.5rem;
                }

                .generation-individuelle .p-steps .p-steps-item {
                    cursor: pointer;
                }
            `}</style>
        </div>
    );
};

export default GenerationIndividuelle;
